import type { SWRConfiguration } from 'swr';

import { useMemo } from 'react';
import useSWR, { mutate } from 'swr';

import axiosInstance, { fetcher, endpoints } from 'src/lib/axios-artham';

// ----------------------------------------------------------------------

export type OtaReleaseStatus = 'DRAFT' | 'STAGED' | 'ROLLING' | 'HALTED' | 'ROLLBACK';

export type OtaRelease = {
  id: string;
  channel: string;
  version: string;
  bundleKey: string;
  status: OtaReleaseStatus;
  rolloutPercent: number;
  /** Backend field name for the rollout percentage (the API returns `rolloutPct`). */
  rolloutPct?: number;
  /** Presigned download URL for the bundle zip; only present on the `active` endpoint. */
  bundleUrl?: string;
  notes?: string;
  createdAt: string;
  stagedAt?: string;
  rolledOutAt?: string;
  haltedAt?: string;
  rolledBackAt?: string;
};

const swrOptions: SWRConfiguration = {
  revalidateIfStale: true,
  revalidateOnFocus: false,
  revalidateOnReconnect: true,
};

export function refreshOtaReleases() {
  mutate((key) => {
    if (Array.isArray(key)) {
      const [url] = key;
      return typeof url === 'string' && url.startsWith('/ota/releases');
    }
    if (typeof key === 'string') return key.startsWith('/ota/releases');
    return false;
  });
}

export function useGetReleases(
  params?: { channel?: string; limit?: number },
  options?: SWRConfiguration
) {
  const { data, error, isLoading } = useSWR<{ success: boolean; data: OtaRelease[] }>(
    [endpoints.ota.releases, { params }],
    fetcher,
    { ...swrOptions, ...options }
  );

  return useMemo(
    () => ({
      releases: data?.data ?? [],
      releasesLoading: isLoading,
      releasesError: error,
    }),
    [data, error, isLoading]
  );
}

export function useGetActiveRelease(channel = 'production', options?: SWRConfiguration) {
  const { releases, releasesLoading, releasesError } = useGetReleases(
    { channel, limit: 5 },
    options
  );
  const active = releases.find((r) => r.status === 'ROLLING' || r.status === 'STAGED') ?? null;
  return { active, activeLoading: releasesLoading, activeError: releasesError };
}

/**
 * Imperative read of the public active-release endpoint. Used by the launch-time
 * OTA check which runs outside React (so no SWR hook). Returns null when nothing
 * is rolling for the channel. The `data` includes a presigned `bundleUrl`.
 */
export async function fetchActiveRelease(channel: string): Promise<OtaRelease | null> {
  const res = await axiosInstance.get<{ success: boolean; data: OtaRelease | null }>(
    endpoints.ota.active,
    { params: { channel } }
  );
  return res.data.data ?? null;
}

export function useGetRelease(id: string | null, options?: SWRConfiguration) {
  const { data, error, isLoading } = useSWR<{ success: boolean; data: OtaRelease }>(
    id ? endpoints.ota.release(id) : null,
    fetcher,
    { ...swrOptions, ...options }
  );

  return useMemo(
    () => ({
      release: data?.data ?? null,
      releaseLoading: isLoading,
      releaseError: error,
    }),
    [data, error, isLoading]
  );
}

export async function getUploadUrl(): Promise<{ bundleKey: string; uploadPath: string }> {
  const res = await axiosInstance.post<{
    success: boolean;
    data: { bundleKey: string; uploadPath: string };
    error?: string;
  }>(endpoints.ota.uploadUrl);
  if (!res.data.success) throw new Error(res.data.error || 'Failed to get upload URL');
  return res.data.data;
}

export async function uploadBundle(bundleKey: string, bytes: ArrayBuffer): Promise<void> {
  await axiosInstance.put(endpoints.ota.uploadBundle, bytes, {
    headers: { 'x-bundle-key': bundleKey, 'Content-Type': 'application/octet-stream' },
  });
}

export async function createRelease(input: {
  channel: string;
  version: string;
  bundleKey: string;
  notes?: string;
}): Promise<OtaRelease> {
  const res = await axiosInstance.post<{ success: boolean; data: OtaRelease; error?: string }>(
    endpoints.ota.releases,
    input
  );
  if (!res.data.success) throw new Error(res.data.error || 'Failed to create release');
  refreshOtaReleases();
  return res.data.data;
}

export async function stageRelease(id: string): Promise<OtaRelease> {
  const res = await axiosInstance.post<{ success: boolean; data: OtaRelease; error?: string }>(
    endpoints.ota.stage(id)
  );
  if (!res.data.success) throw new Error(res.data.error || 'Failed to stage');
  refreshOtaReleases();
  return res.data.data;
}

export async function rolloutRelease(id: string, pct: number): Promise<OtaRelease> {
  const res = await axiosInstance.post<{ success: boolean; data: OtaRelease; error?: string }>(
    endpoints.ota.rollout(id),
    { pct }
  );
  if (!res.data.success) throw new Error(res.data.error || 'Failed to rollout');
  refreshOtaReleases();
  return res.data.data;
}

export async function haltRelease(id: string): Promise<OtaRelease> {
  const res = await axiosInstance.post<{ success: boolean; data: OtaRelease; error?: string }>(
    endpoints.ota.halt(id)
  );
  if (!res.data.success) throw new Error(res.data.error || 'Failed to halt');
  refreshOtaReleases();
  return res.data.data;
}

export async function rollbackRelease(id: string): Promise<OtaRelease> {
  const res = await axiosInstance.post<{ success: boolean; data: OtaRelease; error?: string }>(
    endpoints.ota.rollback(id)
  );
  if (!res.data.success) throw new Error(res.data.error || 'Failed to rollback');
  refreshOtaReleases();
  return res.data.data;
}
